// Extracted from GuestDetail.jsx during the Feb 2026 component split.
// Contains: ShowPrepTab (default export) + ShowPrepAssistantPanel (internal).
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Copy, Sparkles, Check, MessageCircle, RefreshCw, Send, Trash2, Download, X } from "lucide-react";
import { SHOW_FIELD_LABELS } from "./shared";

function ShowPrepTab({ guestId, showPrep, onUpdated }) {
  const intro = showPrep.intro || "";
  const questions = Array.isArray(showPrep.questions) && showPrep.questions.length > 0 ? showPrep.questions : [];
  const hasContent = intro || questions.some(q => q);

  const [draftIntro, setDraftIntro] = useState(intro);
  const [draftQuestions, setDraftQuestions] = useState(questions);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [approvingForGuest, setApprovingForGuest] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const approvedForGuest = !!showPrep.approved_for_guest;

  // Stable per-row IDs so React doesn't reuse DOM state across reorder/delete.
  // Kept in parallel to draftQuestions and mutated by the same handlers.
  const rowIdsRef = useRef([]);
  const ensureIds = (arr) => {
    while (rowIdsRef.current.length < arr.length) {
      rowIdsRef.current.push(
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      );
    }
    if (rowIdsRef.current.length > arr.length) {
      rowIdsRef.current = rowIdsRef.current.slice(0, arr.length);
    }
    return rowIdsRef.current;
  };
  ensureIds(draftQuestions);

  const downloadPdf = async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true); setError("");
    try {
      const res = await api.get(`/guests/${guestId}/show-prep/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `show-prep-${guestId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't build the PDF — try regenerating show prep first.");
    } finally { setDownloadingPdf(false); }
  };

  useEffect(() => {
    setDraftIntro(showPrep.intro || "");
    setDraftQuestions(Array.isArray(showPrep.questions) ? showPrep.questions : []);
  }, [showPrep.intro, showPrep.questions]);

  const applyAiRevision = ({ revised_intro, revised_questions }) => {
    if (typeof revised_intro === "string" && revised_intro.trim()) {
      setDraftIntro(revised_intro);
    }
    if (Array.isArray(revised_questions) && revised_questions.length > 0) {
      setDraftQuestions(revised_questions.slice(0, 8));
    }
    setSavedNote("AI edits applied to draft — remember to Save.");
    setTimeout(() => setSavedNote(""), 3200);
  };

  const dirty = draftIntro !== (showPrep.intro || "") ||
    JSON.stringify(draftQuestions) !== JSON.stringify(showPrep.questions || []);

  const doGenerate = async () => {
    setGenerating(true); setError(""); setSavedNote(""); setShowRegenConfirm(false);
    try {
      const { data } = await api.post(`/guests/${guestId}/show-prep`);
      onUpdated(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "AI drafting failed. Try again.");
    } finally { setGenerating(false); }
  };

  const requestGenerate = () => {
    if (hasContent && (showPrep.edited || dirty)) {
      setShowRegenConfirm(true);
    } else {
      doGenerate();
    }
  };

  const saveEdits = async () => {
    setSaving(true); setError(""); setSavedNote("");
    try {
      const { data } = await api.put(`/guests/${guestId}/show-prep`, {
        intro: draftIntro,
        questions: draftQuestions,
      });
      onUpdated(data);
      setSavedNote("Saved.");
      setTimeout(() => setSavedNote(""), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  const toggleApprovedForGuest = async () => {
    if (approvingForGuest) return;
    setApprovingForGuest(true); setError("");
    try {
      const { data } = await api.put(`/guests/${guestId}/show-prep`, {
        intro: showPrep.intro || "",
        questions: Array.isArray(showPrep.questions) ? showPrep.questions : [],
        approved_for_guest: !approvedForGuest,
      });
      onUpdated(data);
      setSavedNote(!approvedForGuest
        ? "Questions will be shared in the pre-show check-in email."
        : "Questions will no longer be shared with the guest.");
      setTimeout(() => setSavedNote(""), 3500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update sharing setting.");
    } finally { setApprovingForGuest(false); }
  };

  const updateQuestion = (i, val) => setDraftQuestions(draftQuestions.map((q, j) => j === i ? val : q));
  const addQuestion = () => {
    rowIdsRef.current.push(
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    );
    setDraftQuestions([...draftQuestions, ""]);
  };
  const removeQuestion = (i) => {
    rowIdsRef.current = rowIdsRef.current.filter((_, j) => j !== i);
    setDraftQuestions(draftQuestions.filter((_, j) => j !== i));
  };
  const moveQuestion = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= draftQuestions.length) return;
    const next = [...draftQuestions];
    [next[i], next[j]] = [next[j], next[i]];
    const ids = [...rowIdsRef.current];
    [ids[i], ids[j]] = [ids[j], ids[i]];
    rowIdsRef.current = ids;
    setDraftQuestions(next);
  };

  const copyAll = () => {
    const lines = [
      "INTRO",
      draftIntro,
      "",
      "QUESTIONS",
      ...draftQuestions.map((q, i) => `${i + 1}. ${q}`),
    ];
    navigator.clipboard.writeText(lines.join("\n"));
    setSavedNote("Copied to clipboard.");
    setTimeout(() => setSavedNote(""), 2000);
  };

  if (!hasContent && !generating) {
    return (
      <div className="pgm-reveal">
        <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-magenta)", textAlign: "center", padding: 36 }} data-testid="show-prep-empty">
          <div className="pgm-micro">◆ Show Prep</div>
          <h2 className="pgm-display" style={{ fontSize: 28, margin: "8px 0" }}>Draft your intro and questions</h2>
          <p style={{ fontSize: 14, opacity: 0.75, maxWidth: 480, margin: "0 auto 20px" }}>
            AI builds a host intro plus 8 open-ended interview questions from this guest&rsquo;s story material, enrichment, and your show&rsquo;s voice. Every question is editable.
          </p>
          <button className="pgm-btn" onClick={doGenerate} disabled={generating} data-testid="show-prep-generate">
            <Sparkles size={13} /> {generating ? "Drafting…" : "Generate show prep"}
          </button>
          {error && <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)", marginTop: 14 }} data-testid="show-prep-error">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="pgm-reveal" data-testid="show-prep-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div>
          <div className="pgm-micro">◆ Show Prep</div>
          <h2 className="pgm-display" style={{ fontSize: 24, margin: "4px 0 0 0" }}>Intro & interview questions</h2>
          {showPrep.generated_at && (
            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 4 }}>
              Drafted {new Date(showPrep.generated_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
              {showPrep.edited && " · edited"}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="pgm-btn pgm-btn-ghost" onClick={copyAll} data-testid="show-prep-copy-all">
            <Copy size={12} /> Copy all
          </button>
          <button
            className="pgm-btn pgm-btn-ghost"
            onClick={downloadPdf}
            disabled={downloadingPdf}
            data-testid="show-prep-download-pdf"
          >
            <Download size={12} /> {downloadingPdf ? "Building PDF…" : "Download PDF"}
          </button>
          <button
            className="pgm-btn pgm-btn-ghost"
            onClick={() => setChatOpen(v => !v)}
            data-testid="show-prep-ask-ai"
            style={chatOpen ? { borderColor: "var(--pgm-magenta)", color: "var(--pgm-magenta)" } : undefined}
          >
            <MessageCircle size={12} /> {chatOpen ? "Close AI chat" : "Ask AI for help"}
          </button>
          <button className="pgm-btn pgm-btn-ghost" onClick={requestGenerate} disabled={generating} data-testid="show-prep-regenerate">
            <RefreshCw size={12} /> {generating ? "Drafting…" : "Regenerate"}
          </button>
        </div>
      </div>

      {error && <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)", marginBottom: 12 }} data-testid="show-prep-error">{error}</div>}

      <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-magenta)", marginBottom: 14 }}>
        <label className="pgm-label">Host intro (read at the top of the episode)</label>
        <textarea
          className="pgm-textarea"
          style={{ minHeight: 120, fontFamily: "Arial", lineHeight: 1.55 }}
          value={draftIntro}
          onChange={(e) => setDraftIntro(e.target.value)}
          data-testid="show-prep-intro"
        />
      </div>

      <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-magenta)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div className="pgm-micro">◆ Interview questions</div>
          <button className="pgm-btn pgm-btn-ghost" onClick={addQuestion} style={{ padding: "5px 10px", fontSize: 11 }} data-testid="show-prep-add-question">
            <Sparkles size={11} /> Add question
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="show-prep-questions-list">
          {draftQuestions.map((q, i) => (
            <div key={rowIdsRef.current[i] || `q-${i}`} data-testid={`show-prep-question-${i}`} style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "var(--pgm-cream)", padding: 10, borderLeft: "2px solid var(--pgm-olive)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 6 }}>
                <button type="button" className="pgm-btn pgm-btn-ghost" style={{ padding: "2px 6px", fontSize: 10 }} onClick={() => moveQuestion(i, -1)} disabled={i === 0} data-testid={`show-prep-up-${i}`}>▲</button>
                <button type="button" className="pgm-btn pgm-btn-ghost" style={{ padding: "2px 6px", fontSize: 10 }} onClick={() => moveQuestion(i, +1)} disabled={i === draftQuestions.length - 1} data-testid={`show-prep-down-${i}`}>▼</button>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4, letterSpacing: "0.05em", textTransform: "uppercase" }}>Q{i + 1}</div>
                <textarea
                  className="pgm-textarea"
                  style={{ minHeight: 52, fontFamily: "Arial" }}
                  value={q}
                  onChange={(e) => updateQuestion(i, e.target.value)}
                  data-testid={`show-prep-question-input-${i}`}
                />
              </div>
              <button
                type="button"
                className="pgm-btn pgm-btn-ghost"
                style={{ padding: "6px 8px", color: "var(--pgm-orange)", borderColor: "var(--pgm-orange)" }}
                onClick={() => removeQuestion(i)}
                data-testid={`show-prep-remove-${i}`}
                title="Remove question"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="pgm-card" style={{ borderLeftColor: approvedForGuest ? "var(--pgm-olive)" : "var(--pgm-rule)", background: approvedForGuest ? "rgba(164, 165, 16, 0.08)" : "var(--pgm-cream)", marginTop: 14, padding: 14 }} data-testid="show-prep-share-card">
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: approvingForGuest || !hasContent ? "not-allowed" : "pointer" }}>
          <input
            type="checkbox"
            checked={approvedForGuest}
            onChange={toggleApprovedForGuest}
            disabled={approvingForGuest || !hasContent || dirty}
            data-testid="show-prep-share-toggle"
            style={{ marginTop: 3, width: 16, height: 16, accentColor: "var(--pgm-olive)" }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--pgm-ink)", marginBottom: 3 }}>
              Share these questions with the guest in the pre-show check-in email
            </div>
            <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.5 }}>
              {approvedForGuest
                ? "Approved. The 1-week-out check-in email will include these questions so the guest can noodle on answers ahead of time."
                : "Off — the guest never sees the questions. Flip on once the list feels ready."}
              {dirty && (
                <span style={{ display: "block", marginTop: 4, color: "var(--pgm-orange)" }}>
                  Save your unsaved edits first before changing this setting.
                </span>
              )}
            </div>
          </div>
        </label>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: 14 }}>
        {savedNote && <span style={{ fontSize: 12, color: "var(--pgm-olive)" }} data-testid="show-prep-saved-note">{savedNote}</span>}
        <button className="pgm-btn pgm-btn-olive" onClick={saveEdits} disabled={saving || !dirty} data-testid="show-prep-save">
          <Check size={13} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {chatOpen && (
        <ShowPrepAssistantPanel
          guestId={guestId}
          currentIntro={draftIntro}
          currentQuestions={draftQuestions}
          onApplyRevision={applyAiRevision}
          onClose={() => setChatOpen(false)}
        />
      )}

      {showRegenConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(43, 16, 48, 0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} data-testid="show-prep-regen-confirm">
          <div className="pgm-card" style={{ maxWidth: 500, width: "100%", borderLeftColor: "var(--pgm-orange)" }}>
            <div className="pgm-display" style={{ fontSize: 24, marginBottom: 8 }}>Overwrite your edits?</div>
            <p style={{ fontSize: 13.5, lineHeight: 1.55, marginBottom: 18, color: "var(--pgm-ink)" }}>
              You&rsquo;ve edited this show prep. Regenerating will replace the intro and all 8 questions with a fresh AI draft. This can&rsquo;t be undone.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="pgm-btn" style={{ borderColor: "var(--pgm-orange)", background: "var(--pgm-orange)" }} onClick={doGenerate} data-testid="show-prep-regen-confirm-yes">
                <RefreshCw size={13} /> Yes, regenerate
              </button>
              <button className="pgm-btn pgm-btn-ghost" onClick={() => setShowRegenConfirm(false)} data-testid="show-prep-regen-confirm-no">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ShowPrepAssistantPanel({ guestId, currentIntro, currentQuestions, onApplyRevision, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(null); // { revised_intro, revised_questions }
  const [suggestion, setSuggestion] = useState(null);
  const [applied, setApplied] = useState(false);
  const [savingShow, setSavingShow] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const nextMsgs = [...messages, { role: "user", content: text }];
    setMessages(nextMsgs);
    setInput(""); setSending(true); setError(""); setApplied(false); setShowSaved(false);
    try {
      const { data } = await api.post(`/guests/${guestId}/show-prep/chat`, {
        current_intro: revision?.revised_intro || currentIntro || "",
        current_questions: revision?.revised_questions || currentQuestions || [],
        messages: nextMsgs,
      });
      setMessages([...nextMsgs, { role: "assistant", content: data.reply || "" }]);
      if (data.revised_intro || (Array.isArray(data.revised_questions) && data.revised_questions.length)) {
        setRevision({
          revised_intro: data.revised_intro || "",
          revised_questions: Array.isArray(data.revised_questions) ? data.revised_questions : [],
        });
      }
      setSuggestion(data.show_suggestion || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "AI chat failed — try again");
      setMessages(nextMsgs.slice(0, -1));
      setInput(text);
    } finally { setSending(false); }
  };

  const applyRevision = () => {
    if (!revision) return;
    onApplyRevision(revision);
    setApplied(true);
  };

  const applyShowSuggestion = async () => {
    if (!suggestion) return;
    setSavingShow(true); setError("");
    try {
      await api.put("/shows/me", suggestion);
      setShowSaved(true);
      setSuggestion(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update show settings");
    } finally { setSavingShow(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); }
  };

  // Compute a per-question diff so we only preview items that actually changed.
  const diffedQuestions = (() => {
    if (!revision) return [];
    const before = currentQuestions || [];
    const after = revision.revised_questions || [];
    const rows = [];
    const maxLen = Math.max(before.length, after.length);
    for (let i = 0; i < maxLen; i++) {
      const b = (before[i] || "").trim();
      const a = (after[i] || "").trim();
      if (a && a !== b) rows.push({ index: i, before: b, after: a });
    }
    return rows;
  })();

  const introChanged = revision && (revision.revised_intro || "").trim() && (revision.revised_intro || "").trim() !== (currentIntro || "").trim();

  return (
    <div style={{ marginTop: 14, padding: 14, background: "var(--pgm-cream)", borderLeft: "3px solid var(--pgm-magenta)" }} data-testid="show-prep-ai-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--pgm-magenta)" }}>
          <MessageCircle size={13} /> AI Assistant &mdash; Show Prep
        </div>
        <button className="pgm-btn pgm-btn-ghost" style={{ padding: "4px 8px", fontSize: 11 }} onClick={onClose} data-testid="show-prep-ai-close">
          <X size={11} /> Close
        </button>
      </div>

      <div
        ref={scrollRef}
        style={{ maxHeight: 260, overflowY: "auto", background: "#fff", padding: 12, marginBottom: 10, border: "1px solid var(--pgm-rule)" }}
        data-testid="show-prep-ai-history"
      >
        {messages.length === 0 && !sending && (
          <div style={{ fontSize: 12, opacity: 0.7, fontStyle: "italic" }}>
            Ask for tweaks in plain English. Examples: &ldquo;Make the intro warmer&rdquo;, &ldquo;Swap Q3 for something about her first hire&rdquo;, &ldquo;Cut Q7&rdquo;, &ldquo;Make the questions shorter and more open-ended&rdquo;.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ marginBottom: 8, fontSize: 13, lineHeight: 1.5 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: m.role === "user" ? "var(--pgm-wine)" : "var(--pgm-olive)", marginBottom: 2 }}>
              {m.role === "user" ? "You" : "AI"}
            </div>
            <div style={{ whiteSpace: "pre-wrap", color: "var(--pgm-ink)" }}>{m.content}</div>
          </div>
        ))}
        {sending && <div style={{ fontSize: 12, opacity: 0.65 }} data-testid="show-prep-ai-thinking">AI is thinking…</div>}
      </div>

      {revision && !sending && (introChanged || diffedQuestions.length > 0) && (
        <div style={{ marginBottom: 10, padding: 12, background: "#fff", border: "1px dashed var(--pgm-magenta)" }} data-testid="show-prep-ai-revision">
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--pgm-magenta)", marginBottom: 8 }}>Proposed revisions</div>
          {introChanged && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4, color: "var(--pgm-wine)" }}>Intro</div>
              <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Arial", fontSize: 13, lineHeight: 1.55, margin: 0, background: "var(--pgm-cream)", padding: 8, maxHeight: 180, overflowY: "auto" }}>{revision.revised_intro}</pre>
            </div>
          )}
          {diffedQuestions.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4, color: "var(--pgm-wine)" }}>Question changes ({diffedQuestions.length})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto" }}>
                {diffedQuestions.map((row) => (
                  <div key={row.index} style={{ background: "var(--pgm-cream)", padding: 8, fontSize: 12, lineHeight: 1.5 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--pgm-magenta)", marginBottom: 4 }}>Q{row.index + 1}</div>
                    {row.before && (
                      <div style={{ textDecoration: "line-through", opacity: 0.55, marginBottom: 3 }}>{row.before}</div>
                    )}
                    <div style={{ color: "var(--pgm-ink)" }}>{row.after}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button className="pgm-btn" style={{ padding: "6px 12px", fontSize: 11 }} onClick={applyRevision} data-testid="show-prep-ai-apply">
              <Check size={12} /> {applied ? "Applied ✓" : "Apply to draft"}
            </button>
            <button className="pgm-btn pgm-btn-ghost" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => { setRevision(null); setApplied(false); }} data-testid="show-prep-ai-discard">
              <X size={12} /> Discard
            </button>
          </div>
        </div>
      )}

      {suggestion && !sending && (
        <div style={{ marginBottom: 10, padding: 12, background: "#fff", border: "1px dashed var(--pgm-olive)" }} data-testid="show-prep-ai-suggestion">
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--pgm-olive)", marginBottom: 6 }}>Also update your show settings?</div>
          <p style={{ fontSize: 12, opacity: 0.75, margin: "0 0 8px 0" }}>The AI spotted a preference that applies to every episode. Save it to your show so future drafts follow suit.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
            {Object.entries(suggestion).map(([k, v]) => (
              <div key={k} style={{ fontSize: 12 }}>
                <strong>{SHOW_FIELD_LABELS[k] || k}:</strong>
                <div style={{ whiteSpace: "pre-wrap", padding: 6, background: "var(--pgm-cream)", marginTop: 3, fontSize: 12, maxHeight: 120, overflowY: "auto" }}>{String(v)}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="pgm-btn pgm-btn-olive" style={{ padding: "6px 12px", fontSize: 11 }} onClick={applyShowSuggestion} disabled={savingShow} data-testid="show-prep-ai-apply-show">
              <Check size={12} /> {savingShow ? "Saving…" : showSaved ? "Saved ✓" : "Save to show settings"}
            </button>
            <button className="pgm-btn pgm-btn-ghost" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => setSuggestion(null)} disabled={savingShow} data-testid="show-prep-ai-dismiss-show">
              <X size={12} /> Not now
            </button>
          </div>
        </div>
      )}

      {applied && !revision && (
        <div style={{ fontSize: 12, color: "var(--pgm-olive)", marginBottom: 8 }}>Revisions applied to your draft. Don&rsquo;t forget to hit Save changes.</div>
      )}
      {error && <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)", marginBottom: 8 }} data-testid="show-prep-ai-error">{error}</div>}

      <div style={{ display: "flex", gap: 8 }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          rows={2}
          placeholder="Ask for changes… (Ctrl/Cmd + Enter to send)"
          style={{ flex: 1, fontFamily: "Arial", fontSize: 13, lineHeight: 1.5, color: "var(--pgm-ink)", border: "1.5px solid var(--pgm-magenta)", background: "#fff", padding: 8, resize: "vertical", boxSizing: "border-box" }}
          disabled={sending}
          data-testid="show-prep-ai-input"
        />
        <button className="pgm-btn" style={{ padding: "6px 14px", fontSize: 12, alignSelf: "flex-end" }} onClick={send} disabled={sending || !input.trim()} data-testid="show-prep-ai-send">
          <Send size={12} /> Send
        </button>
      </div>
    </div>
  );
}


export default ShowPrepTab;
